import { useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (results: { visits: number; conversions: number; emailsCollected: number; preOrders: number; qualitativeFeedback: string }) => Promise<void>;
}

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  text: '#f0f0ff',
  text2: '#8888aa',
  danger: '#ff4466',
  amber: '#f59e0b',
};

export default function ValidationResultsModal({ isOpen, onClose, onSubmit }: Props) {
  const [visits, setVisits] = useState('');
  const [conversions, setConversions] = useState('');
  const [emailsCollected, setEmailsCollected] = useState('');
  const [preOrders, setPreOrders] = useState('');
  const [qualitativeFeedback, setQualitativeFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showZeroWarning, setShowZeroWarning] = useState(false);

  if (!isOpen) return null;

  const validateFields = (): boolean => {
    const errs: Record<string, string> = {};
    const v = Number(visits) || 0;
    const c = Number(conversions) || 0;
    const e = Number(emailsCollected) || 0;

    if (c > v && v > 0) {
      errs.conversions = 'Conversions cannot exceed visits';
    }
    if (e > v && v > 0) {
      errs.emailsCollected = 'Emails collected cannot exceed visits';
    }
    if (qualitativeFeedback.trim().length > 0 && qualitativeFeedback.trim().length < 20) {
      errs.qualitativeFeedback = `Add at least 20 characters (${qualitativeFeedback.trim().length}/20)`;
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    const v = Number(visits) || 0;
    const c = Number(conversions) || 0;
    const e = Number(emailsCollected) || 0;
    const p = Number(preOrders) || 0;

    // FIX: #5 — warn before submitting empty validation results
    const totalSignals = v + c + e + p;
    const hasQualitative = qualitativeFeedback.trim().length >= 10;
    if (totalSignals === 0 && !hasQualitative) {
      if (!showZeroWarning) {
        setShowZeroWarning(true);
        return;
      }
    }

    setSubmitting(true);
    setError('');
    try {
      await onSubmit({ visits: v, conversions: c, emailsCollected: e, preOrders: p, qualitativeFeedback });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to generate blueprint');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 8, color: C.text, fontSize: 14, padding: '10px 12px',
    outline: 'none', boxSizing: 'border-box',
  };

  const errorInputStyle: React.CSSProperties = {
    ...inputStyle,
    border: `1px solid ${C.danger}`,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 16, padding: 28, maxWidth: 520, width: '90%',
        maxHeight: '85vh', overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Enter Your Validation Results
        </h2>
        <p style={{ fontSize: 13, color: C.text2, marginBottom: 12 }}>
          Log what happened during your 14-day experiment. Your MVP Blueprint will be personalized based on these numbers.
        </p>
        <div style={{
          background: 'rgba(124,111,247,0.06)', border: '1px solid rgba(124,111,247,0.15)',
          borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 11, color: C.text2, lineHeight: 1.5,
        }}>
          Typical results: 100-500 visits, 5-20% conversion rate = strong signal. Even 50 visits with 3 signups is useful data.
        </div>

        {showZeroWarning && (
          <div style={{
            background: `${C.amber}15`, border: `1px solid ${C.amber}40`,
            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          }}>
            <p style={{ color: C.amber, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              All fields are empty or zero
            </p>
            <p style={{ color: C.text2, fontSize: 12, lineHeight: 1.5 }}>
              Your MVP Blueprint will be more useful with real validation data. Are you sure you want to continue with no results?
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => setShowZeroWarning(false)} style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8,
                color: C.text2, fontSize: 12, padding: '6px 14px', cursor: 'pointer',
              }}>Go back and add data</button>
              <button onClick={() => { setShowZeroWarning(false); handleSubmit(); }} style={{
                background: `${C.amber}20`, border: `1px solid ${C.amber}40`, borderRadius: 8,
                color: C.amber, fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer',
              }}>Continue anyway</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 12, marginBottom: 14 }} className="grid grid-cols-1 sm:grid-cols-2">
          {([
            ['visits', 'Total landing page visits', visits, setVisits],
            ['conversions', 'Conversions / signups', conversions, setConversions],
            ['emailsCollected', 'Emails collected', emailsCollected, setEmailsCollected],
            ['preOrders', 'Pre-orders / paid interest', preOrders, setPreOrders],
          ] as [string, string, string, (v: string) => void][]).map(([key, label, val, setter]) => (
            <div key={key}>
              <label style={{ color: C.text2, fontSize: 12, display: 'block', marginBottom: 5 }}>{label}</label>
              <input type="number" min={0} value={val}
                onChange={e => { setter(e.target.value); setShowZeroWarning(false); }}
                style={fieldErrors[key] ? errorInputStyle : inputStyle} placeholder="0" />
              {fieldErrors[key] && <p style={{ color: C.danger, fontSize: 11, marginTop: 3 }}>{fieldErrors[key]}</p>}
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ color: C.text2, fontSize: 12, display: 'block', marginBottom: 5 }}>
            Qualitative feedback (what people said)
            {qualitativeFeedback.length > 0 && qualitativeFeedback.length < 20 && (
              <span style={{ color: C.danger, marginLeft: 8 }}>{qualitativeFeedback.length}/20</span>
            )}
          </label>
          <textarea
            value={qualitativeFeedback}
            onChange={e => { setQualitativeFeedback(e.target.value); setShowZeroWarning(false); }}
            rows={4}
            placeholder="e.g. 'Users loved the concept but said pricing was too high...'"
            style={{ ...(fieldErrors.qualitativeFeedback ? errorInputStyle : inputStyle), resize: 'vertical', fontFamily: 'inherit' }}
          />
          {fieldErrors.qualitativeFeedback && <p style={{ color: C.danger, fontSize: 11, marginTop: 3 }}>{fieldErrors.qualitativeFeedback}</p>}
        </div>

        {error && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            borderRadius: 10, color: C.text2, fontSize: 13, padding: '10px 20px', cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} style={{
            background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
            border: 'none', borderRadius: 10, color: '#fff',
            fontSize: 13, fontWeight: 700, padding: '10px 20px',
            cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
          }}>
            {submitting ? 'Generating Blueprint...' : 'Generate My Blueprint \u2192'}
          </button>
        </div>
      </div>
    </div>
  );
}
